import React, { useState, useEffect, useMemo } from 'react';

const AIFeatureDemos: React.FC = () => {
  const [activeDemo, setActiveDemo] = useState(0);
  const [simulationStep, setSimulationStep] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [chatMessages, setChatMessages] = useState<Array<{role: string; message: string; timestamp: number}>>([]);

  const features = useMemo(() => [
    {
      title: "Real-Time Problem Solving",
      description: "Watch how I analyze and solve problems step-by-step",
      icon: "🔍",
      color: "#ff6b9d",
      demo: "problem-solving"
    },
    {
      title: "Adaptive Learning",
      description: "See how I adjust to your learning style and pace",
      icon: "🧠",
      color: "#4ecdc4",
      demo: "adaptive-learning"
    },
    {
      title: "Interactive Explanations",
      description: "Experience dynamic concept visualization",
      icon: "💡",
      color: "#45b7d1",
      demo: "explanations"
    },
    {
      title: "Progress Tracking",
      description: "Monitor your learning journey with detailed analytics",
      icon: "📊",
      color: "#96ceb4",
      demo: "progress-tracking"
    }
  ], []);

  const problemSolvingSteps = [
    "🤔 Analyzing the problem...",
    "📚 Reviewing relevant concepts...",
    "🎯 Identifying the solution approach...",
    "✏️ Breaking down into steps...",
    "✅ Providing the complete solution!"
  ];

  const adaptiveLearningDemo = [
    { level: "Beginner", content: "Let's start with basic concepts", color: "#96ceb4" },
    { level: "Intermediate", content: "You're ready for more complex problems", color: "#45b7d1" },
    { level: "Advanced", content: "Let's tackle challenging scenarios", color: "#ff6b9d" },
    { level: "Expert", content: "You're mastering this topic!", color: "#feca57" }
  ];

  useEffect(() => {
    const demoTimer = setInterval(() => {
      setActiveDemo(prev => (prev + 1) % 4); // Fixed number since features array is constant
      setSimulationStep(0);
    }, 6000);

    return () => clearInterval(demoTimer);
  }, []);

  useEffect(() => {
    const currentFeature = features[activeDemo];
    if (currentFeature?.demo === 'problem-solving') {
      const stepTimer = setInterval(() => {
        setSimulationStep(prev => {
          if (prev < 4) { // Fixed number since problemSolvingSteps array is constant
            return prev + 1;
          }
          return 0;
        });
      }, 1200);

      return () => clearInterval(stepTimer);
    }
  }, [activeDemo, features]);

  const simulateChat = () => {
    setIsAnimating(true);
    const messages = [
      { role: "student", message: "I'm struggling with quadratic equations", timestamp: Date.now() },
      { role: "ai", message: "I understand! Let's break this down step by step. What specific part is challenging?", timestamp: Date.now() + 1000 },
      { role: "student", message: "I don't know how to factor them", timestamp: Date.now() + 2000 },
      { role: "ai", message: "Perfect! Let me show you a visual method that makes factoring much easier...", timestamp: Date.now() + 3000 }
    ];

    setChatMessages([]);
    messages.forEach((msg, index) => {
      setTimeout(() => {
        setChatMessages(prev => [...prev, msg]);
        if (index === messages.length - 1) {
          setIsAnimating(false);
        }
      }, index * 1500);
    });
  };

  const renderDemo = () => {
    const feature = features[activeDemo];
    
    switch (feature?.demo) {
      case 'problem-solving':
        return (
          <div className="demo-content">
            <div className="problem-box">
              <h4>📝 Solve: x² + 5x + 6 = 0</h4>
              <div className="solution-steps">
                {problemSolvingSteps.slice(0, simulationStep + 1).map((step, index) => (
                  <div 
                    key={index} 
                    className="step-item"
                    style={{
                      animation: `slideIn 0.5s ease ${index * 0.2}s both`,
                      background: `linear-gradient(45deg, ${feature.color}20, transparent)`
                    }}
                  >
                    {step}
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      case 'adaptive-learning':
        return (
          <div className="demo-content">
            <div className="adaptive-demo">
              <h4>🎯 Learning Path Adaptation</h4>
              <div className="learning-levels">
                {adaptiveLearningDemo.map((level, index) => (
                  <div 
                    key={index}
                    className={`level-card ${index === simulationStep % 4 ? 'active' : ''}`}
                    style={{ borderColor: level.color }}
                  >
                    <div className="level-badge" style={{ background: level.color }}>
                      {level.level}
                    </div>
                    <div className="level-content">{level.content}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      case 'explanations':
        return (
          <div className="demo-content">
            <div className="explanation-demo">
              <h4>💡 Interactive Concept Visualization</h4>
              <div className="chat-simulation">
                <button 
                  className="demo-button"
                  onClick={simulateChat}
                  disabled={isAnimating}
                  style={{ background: feature.color }}
                >
                  {isAnimating ? "Demo Running..." : "Start Explanation Demo"}
                </button>
                <div className="chat-messages">
                  {chatMessages.map((msg, index) => (
                    <div 
                      key={index}
                      className={`message ${msg.role}`}
                      style={{
                        animation: `messageSlide 0.5s ease both`,
                        animationDelay: `${index * 0.3}s`
                      }}
                    >
                      <span className="role">{msg.role === 'ai' ? '🤖' : '👤'}</span>
                      <span className="text">{msg.message}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );

      case 'progress-tracking':
        return (
          <div className="demo-content">
            <div className="progress-demo">
              <h4>📊 Learning Analytics Dashboard</h4>
              <div className="metrics-grid">
                <div className="metric-card">
                  <div className="metric-value">87%</div>
                  <div className="metric-label">Comprehension Rate</div>
                  <div className="progress-bar">
                    <div className="progress-fill" style={{ width: '87%', background: feature.color }}></div>
                  </div>
                </div>
                <div className="metric-card">
                  <div className="metric-value">12</div>
                  <div className="metric-label">Topics Mastered</div>
                  <div className="achievement-badges">
                    {[...Array(5)].map((_, i) => (
                      <span key={i} className="badge">🏆</span>
                    ))}
                  </div>
                </div>
                <div className="metric-card">
                  <div className="metric-value">3.2hrs</div>
                  <div className="metric-label">Study Time Today</div>
                  <div className="time-graph">
                    {[...Array(7)].map((_, i) => (
                      <div 
                        key={i} 
                        className="bar" 
                        style={{ 
                          height: `${Math.random() * 60 + 20}px`,
                          background: feature.color 
                        }}
                      ></div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return <div>Demo loading...</div>;
    }
  };

  return (
    <div className="ai-feature-demos">
      <style>{`
        .ai-feature-demos {
          background: linear-gradient(135deg, #1e3c72 0%, #2a5298 100%);
          border-radius: 15px;
          padding: 2rem;
          color: white;
          margin: 2rem 0;
        }

        .demos-header {
          text-align: center;
          margin-bottom: 2rem;
        }

        .demos-title {
          font-size: 2rem;
          font-weight: bold;
          margin-bottom: 0.5rem;
          background: linear-gradient(45deg, #ff6b9d, #4ecdc4);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .feature-tabs {
          display: flex;
          justify-content: center;
          gap: 1rem;
          margin-bottom: 2rem;
          flex-wrap: wrap;
        }

        .feature-tab {
          padding: 0.8rem 1.5rem;
          border-radius: 25px;
          border: 2px solid transparent;
          background: rgba(255, 255, 255, 0.1);
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.9rem;
        }

        .feature-tab.active {
          background: rgba(255, 255, 255, 0.2);
          border-color: currentColor;
          transform: scale(1.05);
        }

        .feature-tab:hover {
          background: rgba(255, 255, 255, 0.15);
          transform: translateY(-2px);
        }

        .demo-container {
          background: rgba(255, 255, 255, 0.05);
          border-radius: 15px;
          padding: 2rem;
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.1);
        }

        .demo-content {
          min-height: 300px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .problem-box, .adaptive-demo, .explanation-demo, .progress-demo {
          width: 100%;
          max-width: 600px;
        }

        .problem-box h4, .adaptive-demo h4, .explanation-demo h4, .progress-demo h4 {
          text-align: center;
          margin-bottom: 1.5rem;
          font-size: 1.3rem;
        }

        .solution-steps {
          space-y: 1rem;
        }

        .step-item {
          padding: 1rem;
          border-radius: 10px;
          margin-bottom: 0.8rem;
          border-left: 4px solid currentColor;
          backdrop-filter: blur(5px);
        }

        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateX(-20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        .learning-levels {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
          gap: 1rem;
        }

        .level-card {
          padding: 1rem;
          border-radius: 10px;
          border: 2px solid transparent;
          background: rgba(255, 255, 255, 0.1);
          text-align: center;
          transition: all 0.3s ease;
        }

        .level-card.active {
          transform: scale(1.1);
          background: rgba(255, 255, 255, 0.2);
          box-shadow: 0 0 20px rgba(255, 255, 255, 0.3);
        }

        .level-badge {
          padding: 0.3rem 0.8rem;
          border-radius: 15px;
          font-size: 0.8rem;
          font-weight: bold;
          color: white;
          margin-bottom: 0.5rem;
          display: inline-block;
        }

        .demo-button {
          padding: 1rem 2rem;
          border: none;
          border-radius: 25px;
          color: white;
          font-weight: bold;
          cursor: pointer;
          transition: all 0.3s ease;
          display: block;
          margin: 0 auto 1.5rem;
        }

        .demo-button:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 5px 15px rgba(0,0,0,0.3);
        }

        .demo-button:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .chat-messages {
          max-height: 200px;
          overflow-y: auto;
          space-y: 0.8rem;
        }

        .message {
          display: flex;
          align-items: start;
          gap: 0.8rem;
          padding: 0.8rem;
          border-radius: 10px;
          margin-bottom: 0.8rem;
        }

        .message.student {
          background: rgba(255, 255, 255, 0.1);
          margin-left: 2rem;
        }

        .message.ai {
          background: rgba(255, 107, 157, 0.2);
          margin-right: 2rem;
        }

        .message .role {
          font-size: 1.2rem;
        }

        .message .text {
          flex: 1;
          line-height: 1.4;
        }

        @keyframes messageSlide {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .metrics-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
          gap: 1.5rem;
        }

        .metric-card {
          background: rgba(255, 255, 255, 0.1);
          padding: 1.5rem;
          border-radius: 10px;
          text-align: center;
        }

        .metric-value {
          font-size: 2rem;
          font-weight: bold;
          margin-bottom: 0.5rem;
        }

        .metric-label {
          font-size: 0.9rem;
          opacity: 0.8;
          margin-bottom: 1rem;
        }

        .progress-bar {
          background: rgba(255, 255, 255, 0.2);
          height: 6px;
          border-radius: 3px;
          overflow: hidden;
        }

        .progress-fill {
          height: 100%;
          border-radius: 3px;
          transition: width 1s ease;
        }

        .achievement-badges {
          display: flex;
          justify-content: center;
          gap: 0.3rem;
        }

        .badge {
          font-size: 1.2rem;
        }

        .time-graph {
          display: flex;
          align-items: end;
          justify-content: center;
          gap: 3px;
          height: 60px;
        }

        .bar {
          width: 8px;
          border-radius: 2px;
          transition: height 0.5s ease;
        }

        @media (max-width: 768px) {
          .ai-feature-demos {
            padding: 1.5rem;
          }
          
          .feature-tabs {
            gap: 0.5rem;
          }
          
          .feature-tab {
            padding: 0.6rem 1rem;
            font-size: 0.8rem;
          }
          
          .demo-container {
            padding: 1.5rem;
          }
          
          .metrics-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>

      <div className="demos-header">
        <h2 className="demos-title">✨ AI Features Preview</h2>
        <p>Experience the future of personalized learning</p>
      </div>

      <div className="feature-tabs">
        {features.map((feature, index) => (
          <div
            key={index}
            className={`feature-tab ${index === activeDemo ? 'active' : ''}`}
            style={{ color: index === activeDemo ? feature.color : 'white' }}
            onClick={() => {
              setActiveDemo(index);
              setSimulationStep(0);
              setChatMessages([]);
            }}
          >
            <span>{feature.icon}</span>
            <span>{feature.title}</span>
          </div>
        ))}
      </div>

      <div className="demo-container">
        <div className="feature-description" style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
          <p style={{ fontSize: '1.1rem', opacity: 0.9 }}>
            {features[activeDemo]?.description}
          </p>
        </div>
        {renderDemo()}
      </div>
    </div>
  );
};

export default AIFeatureDemos;